import React, { useEffect, useReducer, useRef, useState } from 'react';
import { BrowserRouter as Router, Switch, Route, useHistory } from 'react-router-dom';
import PrivateRoute from './components/common/PrivateRoute';
import { routes } from './features/data/constants';
import { initialState, MODAL_TYPES, rootReducer } from './features/data/reducers';

import { initSession, updateSession } from './features/data/session';
import {
  campaignSelectHandler,
  customBlockCreationHandler,
  customBlockEditHandler,
  customBlockSelectHandler,
  dataSetHandler,
  modalCloseHandler,
  pageCreationHandler,
  pageSelectHandler,
  publishLogAddHandler,
  sessionRestrictionModalViewSetHandler,
  sessionTakeOverMessageSetHandler,
  setSession
} from './features/data/actions';
import Login from './features/authentication/Login';
import Page404 from './components/Page404';
import General from './features/settings/General';
import Dealership from './features/settings/Dealership';
import Dealerships from './features/settings/Dealerships';
import { initializePusher } from './pusher';
import { useManualQuery } from 'graphql-hooks';
import { DataQuery } from './features/data/graphql';
import PageCreateModal from './features/pages/PageCreateModal';
import { LoadingIndicator } from './components/common';
import DraftCreateModal from './features/drafts/DraftCreateModal';
import CampaignCreateModal from './features/campaigns/CampaignCreateModal';
import { filter, find, head, values } from 'lodash';
import CustomGeneratedBlockInBuilderEditor from './features/custom-blocks/CustomGeneratedBlockInBuilderEditor';
import PublishModal from './features/builder/components/PublishModal';
import PublishDraftToProductionModal from './features/drafts/PublishDraftToProductionModal';
import Builder from './features/builder/Builder';

import SessionRestrictionModal, { SessionRestrictionModalViews } from './features/session/SessionRestrictionModal';
import { useClientMessages } from './features/session/useClientMessages';
import { useSignOut } from './features/authentication/useSignout';

import Template from './components/Template';
import { default as CampaignsOverview } from './features/campaigns/Overview';
import { default as PagesOverview } from './features/pages/Overview';
import { default as BlogOverview } from './features/blog/Overview';
import { default as AdvertisementsOverview } from './features/advertisements/Overview';
import { default as AdvertisementDetail } from './features/advertisements/Detail';
import { default as VehicleFiltersOverview } from './features/filters/Overview';
import { default as VehicleFiltersDetail } from './features/filters/Detail';
import AdvertisementGroupModal from './features/advertisements/AdvertisementGroupModal';
import { client } from './index';
import { config as themeConfig } from 'dealer-website-components';
import { DefaultPageNames } from './constants';
import { v4 } from 'uuid';

export const StateContext = React.createContext();
export const DispatchContext = React.createContext();

function App() {
  const [state, dispatch] = useReducer(rootReducer, initialState);
  const [currentLanguage, setCurrentLanguage] = useState(undefined);
  const [fetchData, { loading: initialLoading, error: loadingError, data }] = useManualQuery(DataQuery);
  const currentState = state.history[state.history.length - 1];
  const { clientMessageTypes, userChannel, applicationChannel } = useClientMessages();
  const [lastReload, setLastReload] = useState(undefined);
  const { signOut } = useSignOut();

  useEffect(() => {
    if (currentLanguage === undefined) {
      const currentState = state.history[state.history.length - 1];
      if (currentState && currentState.languages.length > 0) {
        const language = head(filter(currentState.languages, (l) => l.available));
        setCurrentLanguage(language.code);
      }
    }
  }, [state]);

  const domainName = () => {
    if (state && state.configuration) {
      let domainName = head(state.configuration.website.domains);
      if (domainName) {
        return domainName;
        //     } else {
        //         if (window) {
        //             try {
        //                 return `${window.location.origin.replace('builder.', '')}`
        //             } catch (e) {
        //                 return ''
        //             }
        //         }
      }
    }

    return '';
  };

  const website = find(state.websites, (w) => w.id === state.currentWebsite);

  const context = {
    culture: currentLanguage,
    builder: true,
    multiLanguage: false,
    mapBoxToken: 'pk.eyJ1IjoiYXV0cmFsaXMiLCJhIjoiY2toeXR2emhwMDk0OTJ6cWh0Y3JjeG5kaSJ9.YCvAWa5Wyqof0wPdueve5w',
    pages: currentState.pages,
    urls: DefaultPageNames,
    customBlocks: currentState.customBlocks,
    campaigns: currentState.campaigns,
    domainName: domainName(),
    defaultPageNames: { ...DefaultPageNames },
    environment: website && !website.isProduction ? 1 : 0
  };

  useEffect(() => {
    window.addEventListener('beforeunload', alertUser);
    // window.addEventListener('unload', handleEndSession)
    return () => {
      window.removeEventListener('beforeunload', alertUser);
      // window.removeEventListener('unload', handleEndSession)
      // handleEndSession()
    };
  }, [state.session]);

  const alertUser = async (e) => {
    e.preventDefault();

    if (state.session.authenticated && window.location.hostname !== 'localhost') {
      await signOut();
      e.returnValue = '';
    }
  };

  const handleReloadPage = () => {
    setLastReload(new Date());
    handleFetchWebsiteData();
  };

  useEffect(() => {
    if (!state.contentTypeDefinitions && state.session && state.session.authenticated) {
      handleFetchWebsiteData();
    }
  }, [state.contentTypeDefinitions, state.session]);

  useEffect(() => {
    // Set home page selected initial
    if (!initialLoading && state.session && state.session.authenticated) {
      if (values(currentState.pages).length > 1) {
        const homeId = head(filter(Object.keys(currentState.pages), (key) => currentState.pages[key].type === 'home'));
        if (homeId) {
          pageSelectHandler(dispatch, homeId);
        }
      }
    }
  }, [initialLoading, state.websites, state.session]);

  const handleFetchWebsiteData = (websiteId = undefined) => {
    const options = {};
    if (websiteId) {
      options['variables'] = { websiteId: websiteId };
    }

    const website = find(state.websites, (w) => w.id === websiteId);
    client.setHeader('Environment', !website || (website && website.isProduction) ? '0' : '1');

    fetchData(options).then((result) => {
      dataSetHandler(dispatch, result.data);
    });
  };

  useEffect(() => {
    async function initializeSession() {
      const session = await initSession();
      setSession(dispatch, session);
    }

    initializeSession();
  }, [lastReload]);

  useEffect(() => {
    if (state.configuration && state.session && state.session.user) {
      initializePusher(state.configuration.company_code, state.session.sessionId);
    }
  }, [state.configuration, state.session]);

  useEffect(() => {
    if (userChannel) {
      userChannel.bind('take_over_request', function (data) {
        switch (data.type) {
          case clientMessageTypes.take_over_request:
            sessionTakeOverMessageSetHandler(dispatch, data, SessionRestrictionModalViews.TakeOverRequest);
            break;
          case clientMessageTypes.take_over_request_accepted:
            sessionTakeOverMessageSetHandler(dispatch, data, SessionRestrictionModalViews.TakeOverRequestAccepted);
            break;
          case clientMessageTypes.take_over_request_declined:
            sessionTakeOverMessageSetHandler(dispatch, data, SessionRestrictionModalViews.TakeOverRequestDeclined);
            break;
          case clientMessageTypes.force_sign_out:
            sessionTakeOverMessageSetHandler(dispatch, data, SessionRestrictionModalViews.ForceSignOut);
            break;
          default:
            break;
        }
      });
    }
  }, [userChannel]);

  const handleAuthenticated = (user, sessionId, otherSessionsCount) => {
    const updatedSession = updateSession(state.session, user, sessionId, otherSessionsCount);
    setSession(dispatch, updatedSession);
  };

  const handleCloseModal = (type) => {
    modalCloseHandler(dispatch, type);
  };

  const handleAddPage = (id, type, url, copyFrom) => {
    const existingPage = find(values(currentState.pages), (page) => page.url === url);

    if (!existingPage) {
      pageCreationHandler(dispatch, id, type, url, copyFrom);
      pageSelectHandler(dispatch, id);
    } else {
      pageSelectHandler(dispatch, existingPage.id);
    }
  };

  const handleAddCustomBlock = (description, culture, data) => {
    customBlockCreationHandler(dispatch, description, culture, data);
  };

  const handleEditCustomBlock = (name, description, culture, data) => {
    customBlockEditHandler(dispatch, name, description, culture, data);
  };

  if (loadingError) {
    return (
      <div className='builder-rounded-md builder-bg-red-50 builder-p-4 builder-m-10'>
        <div className='builder-text-md builder-font-semibold builder-font-medium builder-text-red-800'>Error loading website. Please contact administrator.</div>
      </div>
    );
  }

  if (initialLoading) {
    return <LoadingIndicator />;
  }

  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={state}>
        <Router>
          <React.Fragment>
            {/*ROUTES*/}
            <Switch>
              <Route path={routes.LOGIN}>
                <Login onAuthenticated={handleAuthenticated} />
              </Route>
              <PrivateRoute exact path={routes.HOME}>
                <Template onChangeEnvironment={(websiteId) => handleFetchWebsiteData(websiteId)} context={context}>
                  <Builder context={context} currentLanguage={currentLanguage} setCurrentLanguage={(langCode) => setCurrentLanguage(langCode)} onChangeEnvironment={(websiteId) => handleFetchWebsiteData(websiteId)} />
                </Template>
              </PrivateRoute>
              <PrivateRoute path={`${routes.PAGES}`}>
                <Template onChangeEnvironment={(websiteId) => handleFetchWebsiteData(websiteId)} context={context}>
                  <PagesOverview context={context} currentLanguage={currentLanguage} setCurrentLanguage={(langCode) => setCurrentLanguage(langCode)} />
                </Template>
              </PrivateRoute>
              <PrivateRoute path={`${routes.BLOG}`}>
                <Template onChangeEnvironment={(websiteId) => handleFetchWebsiteData(websiteId)} context={context}>
                  <BlogOverview context={context} currentLanguage={currentLanguage} />
                </Template>
              </PrivateRoute>
              <PrivateRoute path={`${routes.CAMPAIGNS}`}>
                <Template onChangeEnvironment={(websiteId) => handleFetchWebsiteData(websiteId)} context={context}>
                  <CampaignsOverview context={context} />
                </Template>
              </PrivateRoute>
              <PrivateRoute path={`${routes.ADVERTISEMENTS}`}>
                <Template onChangeEnvironment={(websiteId) => handleFetchWebsiteData(websiteId)} context={context}>
                  <AdvertisementsOverview context={context} />
                </Template>
              </PrivateRoute>
              <PrivateRoute path={`${routes.ADVERTISEMENT_DETAIL}/:reference?`}>
                <Template onChangeEnvironment={(websiteId) => handleFetchWebsiteData(websiteId)} context={context}>
                  <AdvertisementDetail context={context} />
                </Template>
              </PrivateRoute>
              <PrivateRoute path={`${routes.VEHICLE_FILTERS}`}>
                <Template onChangeEnvironment={(websiteId) => handleFetchWebsiteData(websiteId)} context={context}>
                  <VehicleFiltersOverview context={context} />
                </Template>
              </PrivateRoute>
              <PrivateRoute path={`${routes.VEHICLE_FILTERS_DETAIL}/:reference?`}>
                <Template onChangeEnvironment={(websiteId) => handleFetchWebsiteData(websiteId)} context={context}>
                  <VehicleFiltersDetail context={context} />
                </Template>
              </PrivateRoute>

              <PrivateRoute exact path={routes.SETTINGS_GENERAL}>
                <General context={context} />
              </PrivateRoute>
              <PrivateRoute path={`${routes.SETTINGS_DEALERSHIPS}`}>
                <Dealerships context={context} />
              </PrivateRoute>
              <PrivateRoute path={`${routes.SETTINGS_DEALERSHIP}/:id`}>
                <Dealership context={context} />
              </PrivateRoute>

              <Route path='*'>
                <Page404 />
              </Route>
            </Switch>

            {/*MODALS*/}
            <DraftCreateModal
              isOpen={state.modal && state.modal.type === MODAL_TYPES.DRAFT_CREATE}
              onCreate={(type, name, copyFrom) => {
                handleCloseModal(MODAL_TYPES.DRAFT_CREATE);
              }}
              onClose={() => handleCloseModal(MODAL_TYPES.DRAFT_CREATE)}
              context={context}
            />

            <PageCreateModal
              isOpen={state.modal && state.modal.type === MODAL_TYPES.PAGE_CREATE}
              type={state.modal && state.modal.payload.type ? state.modal.payload.type : undefined}
              onCreate={(id, type, name, copyFrom) => {
                handleAddPage(id, type, name, copyFrom);
                handleCloseModal(MODAL_TYPES.PAGE_CREATE);
              }}
              onClose={() => handleCloseModal(MODAL_TYPES.PAGE_CREATE)}
              context={context}
            />

            <CampaignCreateModal
              isOpen={state.modal && state.modal.type === MODAL_TYPES.CAMPAIGN_CREATE}
              onClose={() => {
                campaignSelectHandler(dispatch, undefined);
                handleCloseModal(MODAL_TYPES.CAMPAIGN_CREATE);
              }}
              campaign={find(currentState.campaigns, (c) => c.id === currentState.campaignSelected)}
              context={context}
            />

            <CustomGeneratedBlockInBuilderEditor
              isOpen={state.modal?.type === MODAL_TYPES.BLOCK_CREATE}
              onClose={() => {
                customBlockSelectHandler(dispatch, undefined);
                handleCloseModal(MODAL_TYPES.BLOCK_CREATE);
              }}
              languages={currentState.languages}
              context={context}
              customBlock={find(currentState.customBlocks, (cb) => cb.name === currentState.customBlockSelected)}
              onCreate={(description, culture, data) => handleAddCustomBlock(description, culture, data)}
              onEdit={(name, description, culture, data) => handleEditCustomBlock(name, description, culture, data)}
            />

            <PublishModal isOpen={state.modal && state.modal.type === MODAL_TYPES.PUBLISH_WEBSITE} context={context} onClose={() => handleCloseModal(MODAL_TYPES.PUBLISH_WEBSITE)} />

            <PublishDraftToProductionModal
              isOpen={state.modal && state.modal.type === MODAL_TYPES.PUBLISH_DRAFT_TO_PRODUCTION}
              context={context}
              onReload={() => handleReloadPage()}
              onClose={() => handleCloseModal(MODAL_TYPES.PUBLISH_DRAFT_TO_PRODUCTION)}
            />

            <SessionRestrictionModal view={state.sessionRestrictionModalView} onReload={() => handleReloadPage()} context={context} />

            <AdvertisementGroupModal
              isOpen={state.modal && state.modal.type === MODAL_TYPES.ADVERTISEMENT_GROUP_CREATE}
              advertisementGroup={state.modal && state.modal.payload && state.modal.payload.advertisementGroup ? state.modal.payload.advertisementGroup : undefined}
              onClose={() => {
                handleCloseModal(MODAL_TYPES.ADVERTISEMENT_GROUP_CREATE);
              }}
              context={context}
            />
          </React.Fragment>
        </Router>
      </StateContext.Provider>
    </DispatchContext.Provider>
  );
}

export default App;
